import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { sessionKey } from "./Constants/appConstants";
import ApplicationAccess from "./pages/ApplicationAccess/ApplicationAccess";

import { BrowserStorage } from "./utils/browserStorage";
import { Provider } from "react-redux";
import { store } from "./store/store";
import RoleAssignmentRequests from "./pages/RoleAssignmentRequests/RoleAssignmentRequests";
import React, { useEffect, useState } from "react";
import ApplicationList from "./pages/ApplicationList/ApplicationList";
import ApplicationListMain from "./pages/ApplicationList/ApplicationListMain";
import CreateApplication from "./pages/ApplicationList/CreateApplication";
import Admin from "./pages/Admin/Admin";
import CreateEditRoles from "./pages/ApplicationList/CreateEditRoles";
import UserAccess from "./pages/UserManagement/UserAccess/UserAccess";
import UserDetailsPage from "./pages/UserManagement/UserDetailsPage/UserDetailsPage";
import { isAuthorizedUser } from "./utils/commonUtil";
import FeaturesNPermissions from "./pages/UserManagement/FeaturesNPermissions/FeaturesNPermissions";
import UserGroups from "./pages/UserManagement/UserGroups/UserGroups";
import ValueStreams from "./pages/ValueStreams/ValueStreams";
import EditValueStreams from "./pages/ValueStreams/EditValueStreams";
import "./root.module.scss";
import PublishManagementAction from "./Components/PublishHistory/PublishManagement";
import EditFeatureGroup from "./pages/FeatureGroups/EditFeatureGroup";
import FeatureGroups from "./pages/FeatureGroups/FeatureGroup";
import SitLoginScreen from "./Components/SitLoginScreen/SitLoginScreen";
import { useAxiosWithEnv } from "./hooks/useAxiosWithEnv";
import HandleEnvTypeParam from "./HandleEnvTypeParam";
import { getEnv } from "./Constants/env";
import AllAudits from "./pages/UserManagement/AuditHistory/AllAudits";
import DataDomainTable from "./Components/DataDomain/DataDomainTable";
import Dashboard from "./Components/Dashboard/Dashboard";
import ManageCubeAuthVersion from "./Components/CubeAuthVersionList/ManageCubeAuthVersion";
import {
  authUpdatedEvent,
  sessionKey as authSessionKey,
  tokenRefreshFinishedEvent,
} from "./Constants/authConfig";

const RequestApprovalWrapper: React.FC<{
  env: string;
  setEnv: (env: string) => void;
}> = ({ env, setEnv }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isEnvSynced, setIsEnvSynced] = React.useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlEnv = searchParams.get("env");

    if (urlEnv) {
      // If env in URL is different from current env state, update it
      if (urlEnv.toUpperCase() !== env.toUpperCase()) {
        const normalizedEnv = urlEnv.toUpperCase();
        setEnv(normalizedEnv);
        BrowserStorage.setSessionItem("env", normalizedEnv);
      }
      setIsEnvSynced(true);
    } else {
      // If no env in URL, redirect with current env
      navigate(`/request-approval?env=${env}`, { replace: true });
    }
  }, [location.search, env, setEnv, navigate]);

  // Only render Admin after env is synced with URL
  if (!isEnvSynced) {
    return null;
  }

  return <Admin />;
};

const ApplicationListWrapper: React.FC<{
  env: string;
  setEnv: (env: string) => void;
}> = ({ env, setEnv }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isEnvSynced, setIsEnvSynced] = React.useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlEnv = searchParams.get("env");

    if (urlEnv) {
      // If env in URL is different from current env state, update it
      if (urlEnv.toUpperCase() !== env.toUpperCase()) {
        const normalizedEnv = urlEnv.toUpperCase();
        setEnv(normalizedEnv);
        BrowserStorage.setSessionItem("env", normalizedEnv);
      }
      setIsEnvSynced(true);
    } else {
      // If no env in URL, redirect with current env
      navigate(`/application-list?env=${env}`, { replace: true });
    }
  }, [location.search, env, setEnv, navigate]);

  // Only render ApplicationListMain after env is synced with URL
  if (!isEnvSynced) {
    return null;
  }

  return <ApplicationListMain />;
};

const DashboardWrapper: React.FC<{
  env: string;
  setEnv: (env: string) => void;
}> = ({ env, setEnv }) => {
  const location = useLocation();
  const [isEnvSynced, setIsEnvSynced] = React.useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlEnv = searchParams.get("env");

    if (urlEnv && urlEnv.toUpperCase() !== env.toUpperCase()) {
      const normalizedEnv = urlEnv.toUpperCase();
      setEnv(normalizedEnv);
      BrowserStorage.setSessionItem("env", normalizedEnv);
    }
    // Env is now in sync with the URL (or no URL env to apply) — safe to render.
    setIsEnvSynced(true);
  }, [location.search]);

  // Wait until env is synced with the URL before rendering Dashboard so it
  // doesn't fetch once with the stale session env and again with the URL env.
  if (!isEnvSynced) {
    return null;
  }

  return <Dashboard domainName="Dashboard" attributes={[]} />;
};

function App() {
  const [authDetails, setAuthDetails] = useState<string | null>(() =>
    BrowserStorage.getSessionItem(sessionKey),
  );
  const browserEnv = BrowserStorage.getSessionItem("env");
  const globalEnvType = getEnv()?.envtype?.toLowerCase();
  const [loggedInUserRoles, setLoggedInUserRoles] = useState<Array<any>>([]);

  const parseAuthDetails = (value: string | null) => {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  const parsedAuth = parseAuthDetails(authDetails);
  const roles: string[] = parsedAuth?.account?.idTokenClaims?.roles ?? [];
  const isPrivilegedUser = roles.some(
    (role: string) =>
      role.includes("RoleAdmin") ||
      role.includes("AppAdmin") ||
      role.includes("AppRoleApprover"),
  );
  const getDefaultEnv = () => {
    switch (globalEnvType) {
      case "sit":
        return isPrivilegedUser ? "DEV" : "UAT";
      case "dev":
        return "DEV";
      case "qa":
        return "QA";
      case "uat":
        return "UAT";
      case "prod":
        return "PROD";
      default:
        return "DEV";
    }
  };

  const [env, setEnv] = useState<string>(
    browserEnv ? browserEnv : getDefaultEnv(),
  );

  useEffect(() => {
    const syncAuthDetails = () => {
      setAuthDetails(BrowserStorage.getSessionItem(sessionKey));
    };

    const syncOnStorage = (event: StorageEvent) => {
      if (event?.key === sessionKey || event?.key === authSessionKey) {
        syncAuthDetails();
      }
    };

    syncAuthDetails();
    window.addEventListener(authUpdatedEvent, syncAuthDetails);
    window.addEventListener(tokenRefreshFinishedEvent, syncAuthDetails);
    window.addEventListener("focus", syncAuthDetails);
    window.addEventListener("storage", syncOnStorage);

    return () => {
      window.removeEventListener(authUpdatedEvent, syncAuthDetails);
      window.removeEventListener(tokenRefreshFinishedEvent, syncAuthDetails);
      window.removeEventListener("focus", syncAuthDetails);
      window.removeEventListener("storage", syncOnStorage);
    };
  }, []);

  useEffect(() => {
    const storedEnv = BrowserStorage.getSessionItem("env");
    if (storedEnv) {
      setEnv(storedEnv);
    } else {
      setEnv(getDefaultEnv());
    }
  }, [isPrivilegedUser, globalEnvType]);

  useAxiosWithEnv(env ?? "");

  useEffect(() => {
    if (authDetails) {
      const parsedAuthObject = parseAuthDetails(authDetails);
      setLoggedInUserRoles(parsedAuthObject?.idTokenClaims?.roles ?? []);
      return;
    }

    setLoggedInUserRoles([]);
  }, [authDetails]);

  return (
    <Provider store={store}>
      <Router>
        <SitLoginScreen env={env} setEnv={setEnv} />
        <HandleEnvTypeParam env={env} setEnv={setEnv} />
        <Routes>
          {loggedInUserRoles?.length > 0 && (
            <>
              <Route path="/request-access" element={<ApplicationAccess />} />
              <Route
                path="/request-status"
                element={<RoleAssignmentRequests />}
              />
              <Route
                path="/request-approval"
                element={
                  isAuthorizedUser(loggedInUserRoles, [
                    /.*AppRoleApprover|.*AppAdmin|.*RoleAdmin/,
                  ]) ? (
                    <RequestApprovalWrapper env={env} setEnv={setEnv} />
                  ) : (
                    <Navigate to="/Unauthorized" />
                  )
                }
              />
              <Route
                path="/application-list/:id/role/:action/:roleId?"
                element={
                  isAuthorizedUser(loggedInUserRoles, [
                    /.*AppAdmin|.*RoleAdmin/,
                  ]) ? (
                    <CreateEditRoles />
                  ) : (
                    <Navigate to="/Unauthorized" />
                  )
                }
              />
              <Route
                path="/application-list/:id/:type/:action"
                element={
                  isAuthorizedUser(loggedInUserRoles, [
                    /.*AppAdmin|.*RoleAdmin/,
                  ]) ? (
                    <PublishManagementAction />
                  ) : (
                    <Navigate to="/Unauthorized" />
                  )
                }
              />
              <Route
                path="/application-list/:id"
                element={
                  isAuthorizedUser(loggedInUserRoles, [
                    /.*AppAdmin|.*RoleAdmin/,
                  ]) ? (
                    <ApplicationList />
                  ) : (
                    <Navigate to="/Unauthorized" />
                  )
                }
              />
              <Route
                path="/application-list"
                element={
                  // isAuthorizedUser(loggedInUserRoles, [
                  //   /.*AppAdmin|.*RoleAdmin/,
                  // ]) ? (
                  <ApplicationListWrapper env={env} setEnv={setEnv} />
                  // ) : (
                  //   <Navigate to="/Unauthorized" />
                  // )
                }
              />
              <Route
                path="/feature-group"
                element={
                  isAuthorizedUser(loggedInUserRoles, [
                    /.*AppAdmin|.*RoleAdmin/,
                  ]) ? (
                    <FeatureGroups />
                  ) : (
                    <Navigate to="/Unauthorized" />
                  )
                }
              />
              <Route
                path="/value-streams"
                element={
                  isAuthorizedUser(loggedInUserRoles, [
                    /.*AppAdmin|.*RoleAdmin/,
                  ]) ? (
                    <ValueStreams />
                  ) : (
                    <Navigate to="/Unauthorized" />
                  )
                }
              />
              <Route
                path="/dashboard"
                element={
                  isAuthorizedUser(loggedInUserRoles, [
                    /.*AppAdmin|.*RoleAdmin/,
                  ]) ? (
                    <DashboardWrapper env={env} setEnv={setEnv} />
                  ) : (
                    <Navigate to="/Unauthorized" />
                  )
                }
              />

              <Route
                path="/edit-value-streams/:id"
                element={
                  isAuthorizedUser(loggedInUserRoles, [
                    /.*AppAdmin|.*RoleAdmin/,
                  ]) ? (
                    <EditValueStreams />
                  ) : (
                    <Navigate to="/Unauthorized" />
                  )
                }
              />
              <Route
                path="/all-domains"
                element={
                  isAuthorizedUser(loggedInUserRoles, [/.*RoleAdmin/]) ? (
                    <DataDomainTable isEditable={true} />
                  ) : (
                    <Navigate to="/Unauthorized" />
                  )
                }
              />
              <Route
                path="/cube-auth-version-list"
                element={
                  isAuthorizedUser(loggedInUserRoles, [/.*RoleAdmin/]) ? (
                    <ManageCubeAuthVersion
                      appId={0}
                      appName="All Applications"
                      isAccessible={true}
                    />
                  ) : (
                    <Navigate to="/Unauthorized" />
                  )
                }
              />
              <Route
                path="/create-application"
                element={
                  // isAuthorizedUser(loggedInUserRoles, [
                  //   /.*AppAdmin|.*RoleAdmin/,
                  // ]) ? (
                  <CreateApplication />
                  // ) : (
                  //   <Navigate to="/Unauthorized" />
                  // )
                }
              />
              <Route
                path="/user-access"
                element={
                  isAuthorizedUser(loggedInUserRoles, [
                    /.*AppRoleApprover|.*AppAdmin|.*RoleAdmin/,
                  ]) ? (
                    <UserAccess />
                  ) : (
                    <Navigate to="/Unauthorized" />
                  )
                }
              />
              <Route
                path="/audit-history"
                element={
                  isAuthorizedUser(loggedInUserRoles, [
                    /.*AppRoleApprover|.*AppAdmin|.*RoleAdmin/,
                  ]) ? (
                    <AllAudits />
                  ) : (
                    <Navigate to="/Unauthorized" />
                  )
                }
              />
              <Route
                path="/user-access/:workID"
                element={
                  isAuthorizedUser(loggedInUserRoles, [
                    /.*AppRoleApprover|.*AppAdmin|.*RoleAdmin/,
                  ]) ? (
                    <UserDetailsPage />
                  ) : (
                    <Navigate to="/Unauthorized" />
                  )
                }
              />
              <Route
                path="/user-access/:workID/features-permissions"
                element={
                  isAuthorizedUser(loggedInUserRoles, [
                    /.*AppRoleApprover|.*AppAdmin|.*RoleAdmin/,
                  ]) ? (
                    <FeaturesNPermissions />
                  ) : (
                    <Navigate to="/Unauthorized" />
                  )
                }
              />
              <Route
                path="/user-groups"
                element={
                  isAuthorizedUser(loggedInUserRoles, [/.*RoleAdmin/]) ? (
                    <UserGroups />
                  ) : (
                    <Navigate to="/Unauthorized" />
                  )
                }
              />
              <Route
                path="/edit-feature-group/:id"
                element={
                  isAuthorizedUser(loggedInUserRoles, [
                    /.*AppAdmin|.*RoleAdmin/,
                  ]) ? (
                    <EditFeatureGroup />
                  ) : (
                    <Navigate to="/Unauthorized" />
                  )
                }
              />
            </>
          )}
        </Routes>
      </Router>
    </Provider>
  );
}

export default App;
